import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { AuthContext } from '../context/AuthContext';
import { Icon } from '../components/SvgIcons';
import './PaperTemplates.css';

const PaperTemplates = () => {
  const { user } = useContext(AuthContext) || {};
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templatesPage, setTemplatesPage] = useState(1);
  const [templatesPagination, setTemplatesPagination] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    is_shared: false,
    questions: []
  });

  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    templateId: null,
    templateName: '',
    hasDraftPapers: false,
    draftCount: 0,
    draftPapers: []
  });

  useEffect(() => {
    fetchTemplates(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async (page) => {
    try {
      setLoading(true);
      const currentPage = page || templatesPage;
      const params = currentPage > 0 ? { page: currentPage, limit: 10 } : {};
      const { data } = await API.get('/templates', { params });
      setTemplates(data.templates || []);
      if (data.pagination) {
        setTemplatesPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      template_name: '',
      description: '',
      is_shared: false,
      questions: [
        {
          question_number: 'Q1',
          question_type: 'mcq',
          difficulty: 'medium',
          has_subquestions: false,
          subquestions: [],
          marks: 5
        }
      ]
    });
    setExpandedQuestions({});
    setShowForm(true);
  };

  const handleEdit = async (templateId) => {
    try {
      const { data } = await API.get(`/templates/${templateId}`);
      setEditingTemplate(data.template);
      setFormData({
        template_name: data.template.template_name,
        description: data.template.description || '',
        is_shared: data.template.is_shared === 1 || data.template.is_shared === true,
        questions: data.template.questions || []
      });
      // Initialize all questions as expanded by default
      const expanded = {};
      (data.template.questions || []).forEach((_, index) => {
        expanded[index] = true;
      });
      setExpandedQuestions(expanded);
      setShowForm(true);
    } catch (error) {
      console.error('Error loading template:', error);
      showToast('Failed to load template', 'error');
    }
  };

  const handleDelete = async (templateId, templateName) => {
    try {
      // Check if template has unconfirmed (draft) papers
      const { data } = await API.get(`/templates/${templateId}/usage`);

      const hasDraftPapers = data.draft_count > 0;

      if (hasDraftPapers) {
        // Show warning modal for draft papers with list
        setDeleteModal({
          show: true,
          templateId,
          templateName,
          hasDraftPapers: true,
          draftCount: data.draft_count,
          draftPapers: data.draft_papers || []
        });
      } else {
        // No draft papers, show simple confirmation
        setDeleteModal({
          show: true,
          templateId,
          templateName,
          hasDraftPapers: false,
          draftCount: 0,
          draftPapers: []
        });
      }
    } catch (error) {
      console.error('Error with template deletion:', error);
      showToast(error.response?.data?.message || 'Failed to delete template', 'error');
    }
  };

  const confirmDelete = async () => {
    try {
      await API.delete(`/templates/${deleteModal.templateId}`);
      showToast('Template deleted successfully', 'success');
      setDeleteModal({
        show: false,
        templateId: null,
        templateName: '',
        hasDraftPapers: false,
        draftCount: 0,
        draftPapers: []
      });
      fetchTemplates(templatesPage);
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast(error.response?.data?.message || 'Failed to delete template', 'error');
    }
  };

  const subPartMarks = (sq) =>
    (sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0)
      ? sq.subquestions.reduce((s, ssq) => s + (parseInt(ssq.marks) || 0), 0)
      : (parseInt(sq.marks) || 0);

  const getTotalMarks = () => {
    return formData.questions.reduce((sum, q) => {
      if (q.has_subquestions) {
        return sum + q.subquestions.reduce((subSum, sq) => subSum + subPartMarks(sq), 0);
      }
      return sum + (parseInt(q.marks) || 0);
    }, 0);
  };

  const getQuestionMarks = (question) => {
    if (question.has_subquestions) {
      return question.subquestions.reduce((sum, sq) => sum + subPartMarks(sq), 0);
    }
    return parseInt(question.marks) || 0;
  };

  const handleDivideMarks = (questionIndex) => {
    const newQuestions = [...formData.questions];
    const question = newQuestions[questionIndex];

    if (!question.has_subquestions || question.subquestions.length === 0) {
      showToast('Please add sub-questions first', 'warning');
      return;
    }

    // Prompt for total marks to divide
    const totalMarks = prompt(
      `Enter total marks to divide among ${question.subquestions.length} sub-questions:`,
      getQuestionMarks(question) || 10
    );

    if (totalMarks === null) return; // User cancelled

    const marks = parseInt(totalMarks);
    if (isNaN(marks) || marks <= 0) {
      showToast('Please enter a valid positive number', 'error');
      return;
    }

    // Divide marks evenly
    const numSubQuestions = question.subquestions.length;
    const marksPerSub = Math.floor(marks / numSubQuestions);
    const remainder = marks % numSubQuestions;

    // Distribute marks (first sub-question gets remainder)
    newQuestions[questionIndex].subquestions = question.subquestions.map((sq, i) => ({
      ...sq,
      marks: i === 0 ? marksPerSub + remainder : marksPerSub
    }));

    setFormData({ ...formData, questions: newQuestions });
    showToast(`Marks divided: ${marks} marks distributed among ${numSubQuestions} sub-questions`, 'success');
  };

  const toggleQuestionExpanded = (questionIndex) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionIndex]: !prev[questionIndex]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.template_name.trim()) {
      showToast('Please enter a template name', 'error');
      return;
    }

    if (formData.questions.length === 0) {
      showToast('Please add at least one question', 'error');
      return;
    }

    try {
      if (editingTemplate) {
        await API.put(`/templates/${editingTemplate.template_id}`, formData);
        showToast('Template updated successfully', 'success');
      } else {
        await API.post('/templates', formData);
        showToast('Template created successfully', 'success');
      }
      setShowForm(false);
      fetchTemplates(templatesPage);
    } catch (error) {
      console.error('Error saving template:', error);
      showToast('Failed to save template', 'error');
    }
  };

  const handleAddQuestion = () => {
    const nextNumber = formData.questions.length + 1;
    const newQuestion = {
      question_number: `Q${nextNumber}`,
      question_type: 'mcq',
      difficulty: 'medium',
      has_subquestions: false,
      subquestions: [],
      marks: 5
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    const renumbered = newQuestions.map((q, i) => ({
      ...q,
      question_number: `Q${i + 1}`
    }));
    setFormData({ ...formData, questions: renumbered });
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    if (field === 'has_subquestions') {
      if (value && newQuestions[index].subquestions.length === 0) {
        newQuestions[index].subquestions = [
          { sub_number: 'a', marks: 2 }
        ];
      }
    }
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleAddSubQuestion = (questionIndex) => {
    const newQuestions = [...formData.questions];
    const subCount = newQuestions[questionIndex].subquestions.length;
    const nextLetter = String.fromCharCode(97 + subCount);
    const previousSubQuestion = subCount > 0
      ? newQuestions[questionIndex].subquestions[subCount - 1]
      : null;
    const newSubQuestion = previousSubQuestion
      ? { ...previousSubQuestion, sub_number: nextLetter }
      : { sub_number: nextLetter, marks: 2 };
    newQuestions[questionIndex].subquestions.push(newSubQuestion);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveSubQuestion = (questionIndex, subIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].subquestions = newQuestions[questionIndex].subquestions.filter((_, i) => i !== subIndex);
    newQuestions[questionIndex].subquestions = newQuestions[questionIndex].subquestions.map((sq, i) => ({
      ...sq,
      sub_number: String.fromCharCode(97 + i)
    }));
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleSubQuestionChange = (questionIndex, subIndex, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].subquestions[subIndex] = {
      ...newQuestions[questionIndex].subquestions[subIndex],
      [field]: value
    };
    setFormData({ ...formData, questions: newQuestions });
  };

  const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
  const romanFor = (i) => ROMAN[i] || String(i + 1);

  const handleAddSubPart = (questionIndex, subIndex) => {
    const newQuestions = [...formData.questions];
    const sq = newQuestions[questionIndex].subquestions[subIndex];
    sq.subquestions = sq.subquestions || [];
    sq.has_subquestions = true;
    sq.subquestions.push({
      sub_number: romanFor(sq.subquestions.length),
      marks: 1,
      difficulty: sq.difficulty || 'medium'
    });
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveSubPart = (questionIndex, subIndex, partIndex) => {
    const newQuestions = [...formData.questions];
    const sq = newQuestions[questionIndex].subquestions[subIndex];
    sq.subquestions = sq.subquestions
      .filter((_, i) => i !== partIndex)
      .map((ssq, i) => ({ ...ssq, sub_number: romanFor(i) }));
    if (sq.subquestions.length === 0) sq.has_subquestions = false;
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleSubPartChange = (questionIndex, subIndex, partIndex, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].subquestions[subIndex].subquestions[partIndex] = {
      ...newQuestions[questionIndex].subquestions[subIndex].subquestions[partIndex],
      [field]: value
    };
    setFormData({ ...formData, questions: newQuestions });
  };

  const cancelDelete = () => {
    setDeleteModal({
      show: false,
      templateId: null,
      templateName: '',
      hasDraftPapers: false,
      draftCount: 0,
      draftPapers: []
    });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Icon name="Clipboard" size={28} /> <span>Paper Templates</span></h1>
          <p className="dashboard-subtitle" style={{ color: '#ffffff' }}>
            Create templates with sub-questions
          </p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button className="btn btn-success" onClick={() => navigate('/extract-template')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <Icon name="Upload" size={16} /> Upload PDF Reference
          </button>
          <button className="btn btn-primary" onClick={handleCreateNew} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <Icon name="Plus" size={16} /> Create New Template
          </button>
        </div>
      </div>

      {showForm ? (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {editingTemplate
                ? <><Icon name="Edit" size={18} /> <span>Edit Template</span></>
                : <><Icon name="Plus" size={18} /> <span>Create New Template</span></>}
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit"
                className="btn btn-sm"
                form="template-form"
                style={{
                  background: '#ffffff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Icon name="X" size={14} /> Cancel
              </button>
            </div>
          </div>

          <form id="template-form" onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Template Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                placeholder="e.g., Standard Exam Template"
                required
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this template..."
                rows={2}
              />
            </div>

            {user?.role === 'examiner' && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', background: 'var(--bg-secondary)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Share with other examiners</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>Other examiners can view and use this template</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_shared} onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{
                    position: 'absolute', inset: 0, borderRadius: '12px', transition: '0.3s',
                    backgroundColor: formData.is_shared ? '#3b82f6' : '#cbd5e1'
                  }}>
                    <span style={{
                      position: 'absolute', left: formData.is_shared ? '22px' : '3px', top: '3px',
                      width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: '0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                  </span>
                </label>
              </div>
            )}

            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 className="form-section-title">Questions Structure</h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span className="total-marks-badge">
                    {formData.questions.length} Questions | {getTotalMarks()} Marks
                  </span>
                  <button type="button" className="btn-add-question" onClick={handleAddQuestion} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Icon name="Plus" size={14} /> Add Question
                  </button>
                </div>
              </div>

              <div className="template-questions-list">
                {formData.questions.map((q, qIndex) => (
                  <div key={qIndex} className="template-question-item">
                    {/* Main Question */}
                    <div
                      className="question-header"
                      style={{
                        cursor: q.has_subquestions ? 'pointer' : 'default',
                        position: 'relative'
                      }}
                    >
                      {q.has_subquestions && (
                        <span
                          onClick={() => toggleQuestionExpanded(qIndex)}
                          style={{
                            position: 'absolute',
                            left: '-20px',
                            fontSize: '1.2rem',
                            transition: 'transform 0.2s',
                            transform: expandedQuestions[qIndex] ? 'rotate(90deg)' : 'rotate(0deg)'
                          }}
                        >
                          ▶
                        </span>
                      )}

                      <span
                        className="question-number-badge"
                        onClick={() => q.has_subquestions && toggleQuestionExpanded(qIndex)}
                      >
                        {q.question_number}
                      </span>

                      <div
                        style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center' }}
                        onClick={(e) => {
                          // Only toggle if clicking on empty space, not on input
                          if (e.target === e.currentTarget && q.has_subquestions) {
                            toggleQuestionExpanded(qIndex);
                          }
                        }}
                      >
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '600' }}>Type:</span>
                        <input
                          type="text"
                          className="form-input"
                          value={q.question_type}
                          onChange={(e) => handleQuestionChange(qIndex, 'question_type', e.target.value)}
                          placeholder="e.g., mcq, short_answer, case_study"
                          list={`question-types-${qIndex}`}
                          style={{ flex: 1, padding: '0.5rem' }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <datalist id={`question-types-${qIndex}`}>
                          <option value="mcq">Multiple Choice (MCQ)</option>
                          <option value="short_answer">Short Answer</option>
                          <option value="long_answer">Long Answer</option>
                          <option value="true_false">True/False</option>
                          <option value="fill_in_blanks">Fill in the Blanks</option>
                          <option value="matching">Match the Following</option>
                          <option value="numerical">Numerical/Calculate</option>
                          <option value="diagram">Diagram/Draw</option>
                          <option value="case_study">Case Study</option>
                          <option value="practical">Practical</option>
                          <option value="coding">Coding</option>
                        </datalist>
                      </div>

                      {!q.has_subquestions && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {/* Difficulty Selector for Main Question (no sub-questions) */}
                          <select
                            className="form-input"
                            value={q.difficulty || 'medium'}
                            onChange={(e) => handleQuestionChange(qIndex, 'difficulty', e.target.value)}
                            style={{ padding: '0.5rem', fontSize: '0.875rem', minWidth: '110px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="easy">Easy</option>
                            <option value="medium">Medium</option>
                            <option value="hard">Hard</option>
                          </select>

                          <input
                            type="number"
                            className="marks-input"
                            value={q.marks}
                            onChange={(e) => handleQuestionChange(qIndex, 'marks', parseInt(e.target.value))}
                            min="1"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span style={{ fontWeight: '600', color: 'var(--success)' }}>marks</span>
                        </div>
                      )}

                      {q.has_subquestions && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '600' }}>
                            ({getQuestionMarks(q)} marks)
                          </span>
                        </div>
                      )}

                      <label className="checkbox-label" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={q.has_subquestions}
                          onChange={(e) => {
                            handleQuestionChange(qIndex, 'has_subquestions', e.target.checked);
                            if (e.target.checked) {
                              setExpandedQuestions(prev => ({ ...prev, [qIndex]: true }));
                            }
                          }}
                        />
                        <span>Has Sub-questions</span>
                      </label>

                      <button
                        type="button"
                        className="btn-delete-question"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveQuestion(qIndex);
                        }}
                        title="Delete Question"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <Icon name="Trash" size={14} /> Delete
                      </button>
                    </div>

                    {/* Sub-questions */}
                    {q.has_subquestions && expandedQuestions[qIndex] !== false && (
                      <div className="subquestions-section">
                        <div className="subquestions-header">
                          <span className="subquestions-title">
                            Sub-questions ({getQuestionMarks(q)} marks total)
                          </span>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              type="button"
                              className="btn-divide-marks"
                              onClick={() => handleDivideMarks(qIndex)}
                              title="Divide marks evenly among sub-questions"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                            >
                              <Icon name="Divide" size={14} /> Divide Marks
                            </button>
                            <button
                              type="button"
                              className="btn-add-subquestion"
                              onClick={() => handleAddSubQuestion(qIndex)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                            >
                              <Icon name="Plus" size={14} /> Add Sub-question
                            </button>
                          </div>
                        </div>

                        {q.subquestions.map((sq, sqIndex) => {
                          const hasParts = sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0;
                          return (
                            <div key={sqIndex} className="subquestion-block">
                              <div className="subquestion-item">
                                <span className="subquestion-number">
                                  {sq.sub_number}
                                </span>

                                <span className="subquestion-type">
                                  {q.question_type}
                                </span>

                                {!hasParts && (
                                  <select
                                    className="form-input"
                                    value={sq.difficulty || 'medium'}
                                    onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'difficulty', e.target.value)}
                                    style={{ padding: '0.4rem', fontSize: '0.75rem', minWidth: '100px' }}
                                  >
                                    <option value="easy">Easy</option>
                                    <option value="medium">Medium</option>
                                    <option value="hard">Hard</option>
                                  </select>
                                )}

                                <div className="subquestion-marks">
                                  <input
                                    type="number"
                                    className="marks-input"
                                    value={hasParts ? subPartMarks(sq) : sq.marks}
                                    onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'marks', parseInt(e.target.value))}
                                    min="1"
                                    disabled={hasParts}
                                    title={hasParts ? 'Total of sub-parts' : undefined}
                                  />
                                  <span style={{ fontWeight: '600', color: 'var(--success)' }}>marks</span>
                                </div>

                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handleAddSubPart(qIndex, sqIndex)}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                                  title="Add a sub-part (third level)"
                                >
                                  <Icon name="Plus" size={14} /> Sub-part
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleRemoveSubQuestion(qIndex, sqIndex)}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                  aria-label="Remove sub-question"
                                >
                                  <Icon name="X" size={14} />
                                </button>
                              </div>

                              {hasParts && (
                                <div className="subparts-section" style={{ marginLeft: '1.5rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                  {sq.subquestions.map((ssq, partIndex) => (
                                    <div key={partIndex} className="subquestion-item">
                                      <span className="subquestion-number">{sq.sub_number}.{ssq.sub_number}</span>
                                      <span className="subquestion-type">{q.question_type}</span>
                                      <select
                                        className="form-input"
                                        value={ssq.difficulty || 'medium'}
                                        onChange={(e) => handleSubPartChange(qIndex, sqIndex, partIndex, 'difficulty', e.target.value)}
                                        style={{ padding: '0.4rem', fontSize: '0.75rem', minWidth: '100px' }}
                                      >
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                      </select>
                                      <div className="subquestion-marks">
                                        <input
                                          type="number"
                                          className="marks-input"
                                          value={ssq.marks}
                                          onChange={(e) => handleSubPartChange(qIndex, sqIndex, partIndex, 'marks', parseInt(e.target.value))}
                                          min="1"
                                        />
                                        <span style={{ fontWeight: '600', color: 'var(--success)' }}>marks</span>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleRemoveSubPart(qIndex, sqIndex, partIndex)}
                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                        aria-label="Remove sub-part"
                                      >
                                        <Icon name="X" size={14} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {q.subquestions.length === 0 && (
                          <div className="empty-subquestions">
                            No sub-questions yet. Click "Add Sub-question" to add one.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {formData.questions.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                    No questions yet. Click "Add Question" to start building your template.
                  </p>
                )}
              </div>
            </div>


          </form>
        </div>
      ) : (
        <div className="templates-grid">
          {templates.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><Icon name="Clipboard" size={48} /></div>
              <p>No templates yet</p>
              <button className="btn btn-primary btn-sm" onClick={handleCreateNew}>
                Create Your First Template
              </button>
            </div>
          ) : (
            templates.map(template => (
              <div key={template.template_id} className="template-card">
                <div className="template-card-header">
                  <h3>{template.template_name}</h3>
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {template.is_default === 1 && (
                      <span className="badge badge-success">Default</span>
                    )}
                    {template.is_shared === 1 && template.created_by !== user?.user_id && (
                      <span className="badge badge-info" style={{ background: '#dbeafe', color: '#1e40af' }}>Shared</span>
                    )}
                    {template.is_shared === 1 && template.created_by === user?.user_id && (
                      <span className="badge badge-info" style={{ background: '#d1fae5', color: '#065f46' }}>Sharing ON</span>
                    )}
                  </div>
                </div>

                {template.description && (
                  <p className="template-description">{template.description}</p>
                )}

                <div className="template-stats">
                  <div className="stat">
                    <span className="stat-label">Questions:</span>
                    <span className="stat-value">{template.question_count || 0}</span>
                  </div>
                </div>

                <div className="template-actions">
                  {template.created_by === user?.user_id ? (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleEdit(template.template_id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <Icon name="Edit" size={14} /> Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(template.template_id, template.template_name)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                      >
                        <Icon name="Trash" size={14} /> Delete
                      </button>
                    </>
                  ) : (
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>View only</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {templatesPagination && templatesPagination.totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1.5rem 0'
        }}>
          <button
            className="btn btn-sm"
            onClick={() => {
              const prevPage = templatesPage - 1;
              setTemplatesPage(prevPage);
              fetchTemplates(prevPage);
            }}
            disabled={templatesPage <= 1 || loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              fontSize: '0.8125rem',
              opacity: templatesPage <= 1 || loading ? 0.5 : 1,
              cursor: templatesPage <= 1 || loading ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            Page {templatesPage} of {templatesPagination.totalPages} ({templatesPagination.total} total)
          </span>
          <button
            className="btn btn-sm"
            onClick={() => {
              const nextPage = templatesPage + 1;
              setTemplatesPage(nextPage);
              fetchTemplates(nextPage);
            }}
            disabled={templatesPage >= templatesPagination.totalPages || loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem 0.75rem',
              fontSize: '0.8125rem',
              opacity: templatesPage >= templatesPagination.totalPages || loading ? 0.5 : 1,
              cursor: templatesPage >= templatesPagination.totalPages || loading ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Icon name="Warning" size={20} /> <span>Confirm Delete</span></h2>
              <button className="modal-close" onClick={cancelDelete} aria-label="Close"><Icon name="X" size={18} /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this template?</p>
              <p style={{ fontWeight: 'bold', marginTop: '0.5rem' }}>"{deleteModal.templateName}"</p>

              {deleteModal.hasDraftPapers ? (
                <>
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'var(--warning-light)',
                    border: '2px solid var(--warning)',
                    borderRadius: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ display: 'inline-flex', color: 'var(--warning)' }}><Icon name="Warning" size={20} /></span>
                      <strong style={{ color: 'var(--warning)' }}>Warning!</strong>
                    </div>
                    <p style={{ margin: '0.5rem 0', fontSize: '0.875rem' }}>
                      This template is being used by <strong>{deleteModal.draftCount}</strong> unconfirmed (draft) paper(s).
                    </p>
                    <p style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.875rem',
                      color: 'var(--danger)',
                      fontWeight: '600'
                    }}>
                      You must first confirm these papers before deleting this template.
                    </p>
                  </div>

                  {/* List of Draft Papers */}
                  {deleteModal.draftPapers.length > 0 && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '0.5rem',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Icon name="FileText" size={14} /> <span>Draft Papers Using This Template:</span>
                      </h4>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                        {deleteModal.draftPapers.map((paper) => (
                          <li key={paper.paper_id} style={{ marginBottom: '0.5rem' }}>
                            <strong>{paper.paper_name}</strong>
                            {paper.subject && <span style={{ color: 'var(--text-secondary)' }}> - {paper.subject}</span>}
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                              Created: {new Date(paper.created_at).toLocaleDateString()}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ color: 'var(--danger)', marginTop: '1rem', fontSize: '0.875rem' }}>
                  This action cannot be undone.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelDelete}>
                {deleteModal.hasDraftPapers ? 'OK' : 'Cancel'}
              </button>
              {!deleteModal.hasDraftPapers && (
                <button
                  className="btn btn-danger"
                  onClick={confirmDelete}
                >
                  Delete Template
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperTemplates;